import { useContext, useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import yup from 'utils/yup';
import { getDays, getMonths, isValidDay, getBirthdayYearOptions } from 'utils/date';
import { diContainerContext } from 'application/contexts/useDiContainer';
import { CompanyUserQuery } from 'interfaceAdapter/queries/CompanyUser';
import { CompanyUserRepository } from 'interfaceAdapter/repositories/CompanyUser';
import { companyUserContext } from 'application/contexts/useCompanyUser';
import { useNavigate } from 'react-router-dom';
import { useLoading } from 'application/contexts/useLoading';

export const schema = yup.object().shape({
    first_name: yup.string().required().max(50),
    last_name: yup.string().required().max(50),
    first_name_kana: yup.string().required().max(50),
    last_name_kana: yup.string().required().max(50),
    icon_image: yup.string().nullable().defined(),
    department: yup.string().nullable().defined().default(''),
    job: yup.string().nullable().defined().default(''),
    gender: yup.number().required(),
    phone_number: yup.string().required().max(30).onlyNumber().defined(),
    year: yup.number().required(),
    month: yup.number().required(),
    day: yup.number().required(),
});

type CompanyUserForm = yup.InferType<typeof schema>;

export const useCompanyUserProfileRegistration = () => {
    const diContainer = useContext(diContainerContext);
    const companyUserQuery = diContainer.resolve(CompanyUserQuery);
    const companyUserRepository = diContainer.resolve(CompanyUserRepository);
    const { loading, setLoading } = useLoading();
    const [showSnackbar, setShowSnackbar] = useState(false);
    const { user, notCompletedProfile } = useContext(companyUserContext);
    const navigate = useNavigate();

    const {
        control,
        handleSubmit,
        formState: { errors },
        getValues,
        watch,
        setValue,
        setError,
        clearErrors,
    } = useForm<CompanyUserForm>({
        resolver: yupResolver(schema),
        mode: 'onChange',
        defaultValues: {
            icon_image: null,
            year: 1920,
            month: 1,
            day: 1,
            job: '',
            department: '',
        },
    });

    useEffect(() => {
        const checkCanEnter = async () => {
            const { status } = await companyUserQuery.checkToken(true);

            if (status === false) {
                // 有効なトークンが無いのでリダイレクト
                navigate('/company/login/');
            }
        };
        checkCanEnter();
    }, [window.location]);

    useEffect(() => {
        if (user) {
            setValue('first_name', user.first_name);
            setValue('last_name', user.last_name);
            setValue('first_name_kana', user.first_name_kana ?? '');
            setValue('last_name_kana', user.last_name_kana ?? '');
            setValue('icon_image', user.icon_image);
            setValue('department', user.department);
            setValue('job', user.job);
            setValue('gender', user.gender);
            setValue('phone_number', user.phone_number ?? '');
            if (user.birthday) {
                const [year, month, day] = user.birthday.split('-').map((v) => parseInt(v, 10));
                setValue('year', year);
                setValue('month', month);
                setValue('day', day);
            }
        }
    }, [user]);

    const submit = handleSubmit(async (data) => {
        setLoading(true);
        try {
            const birthday = `${data.year}-${data.month}-${data.day}`;

            await companyUserRepository.registerProfile({
                ...data,
                birthday,
            });
            setShowSnackbar(true);
            // TODO 確認画面の実装をサボっている
            setTimeout(() => (window.location.href = '/company/dashboard/'), 1500);
        } finally {
            setLoading(false);
        }
    });

    useEffect(() => {
        // 生年月日の組み合わせに対するバリデーション
        const subscription = watch((value, { name }) => {
            if (!['year', 'month', 'day'].includes(name ?? '')) {
                return;
            }
            const { year = -1, month = -1, day = -1 } = value;
            if (value.year === -1 || value.month === -1 || value.day === -1) {
                return;
            }
            if (!isValidDay(year, month, day)) {
                // 便宜上yearのエラーとして登録
                setError('year', { message: '正しい日付を入力してください。' });
            } else {
                clearErrors('year');
            }
        });

        return () => subscription.unsubscribe();
    }, []);

    const yearOptions = getBirthdayYearOptions();
    const monthOptions = getMonths();
    const dayOptions = getDays();

    return {
        control,
        errors,
        loading,
        showSnackbar,
        yearOptions,
        monthOptions,
        dayOptions,
        notCompletedProfile,
        setShowSnackbar,
        submit,
        getValues,
        setValue,
        watch,
    };
};
